import React, {useMemo, useRef, useState, useEffect, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import SaleStatusModal from './SaleStatusModal'
import {Table, Spinner, OverlayTrigger, Tooltip} from 'react-bootstrap'
import ActionMenuPortal from './ActionMenuPortal'
import {Contact, STATUS_CONTACT} from '../type'
import {ContactTableHeader} from './ContactTableHeader'
import {TableColumn, ContactTableProps} from './types'
import {
  FIELD_EMAIL,
  FIELD_PHONE,
  FIELD_SOURCE_DETAIL,
  FIELD_ACTION,
  FIELD_SELECTED,
  FIELD_TAGS,
  FIELD_PROMO_ACTION,
  FIELD_STATUS,
  FIELD_SALES,
  FIELD_REPLIES,
  FIELD_FLOW,
  FIELD_ASSIGN_TO,
  // New fields according to feedback
  FIELD_CARD_OFFERS,
  FIELD_INTENT,
  FIELD_VOICE_NOTES_SUMMARY,
  FIELD_NOTES_PREVIEW,
  FIELD_LAST_REPLY,
  FIELD_TOTAL_MESSAGES_SENT,
  FIELD_SENTIMENT,
  FIELD_SOURCE,
  FIELD_FULL_NAME,
  FIELD_OUTBOUND_MESSAGE,
} from '../constants'
import {KTSVG} from '../../../../../_metronic/helpers'
import {MenuComponent} from '../../../../../_metronic/assets/ts/components'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import {useContactEdit} from '../hooks/useContactEdit'
import {ContactEditModal} from './ContactEditModal'
import {PromoAction} from '../../../../../services/cellcast.v2.types'
import {AuthenticateUser} from '../../../../Utils/_helpers/RoleAndPermission'
import QuickMessageDrawerInner from '../../../../../_metronic/partials/layout/drawer-messenger/Quickmessge'
import {toast} from 'react-toastify'
import VoiceRecordModal from '../VoiceRecordModal'

export const getAttentionStates = (contact: Contact) => {
  const hasVoiceNote = contact?.lastVoice?.id ? true : false

  const getPulseState = () => {
    if (contact.sales) {
      return false
    }
    return !hasVoiceNote
  }
  const shouldPulseVoice = getPulseState()

  // Helper: parse ISO or timestamp-like string safely
  const parseTime = (value?: string | null): number | null => {
    if (!value) return null
    const t = Date.parse(value as string)
    return Number.isNaN(t) ? null : t
  }

  const lastReplyAt = parseTime((contact as any).lastReplyMessageTime)
  const outboundAt = parseTime((contact as any).outboundMessageTime)
  const callAt = parseTime((contact as any).callLastTime)
  const hasNewBite = lastReplyAt !== null
  const noMessage = outboundAt === null || outboundAt <= (lastReplyAt as number)
  const noCall = callAt === null || callAt <= (lastReplyAt as number)

  // Pulse the message button when there's a new bite and no message or call action taken
  const shouldPulseMessage = hasNewBite && noMessage && noCall

  return {shouldPulseVoice, shouldPulseMessage}
}
export const ContactTable: React.FC<ContactTableProps> = ({
  contacts,
  columns,
  isLoading,
  noDataMessage,
  minHeight,
  sortKey,
  sortDirection,
  onSort,
  selectedContacts,
  onSelectContact,
  selectedAll,
  onSelectAll,
  onDeleteContacts,
  setSelectedContacts,
  onContactUpdated,
  onColumnReorder,
  onToggleSale,
  scrollOnTop,
}) => {
  const {t} = useI18n()
  const {
    isEditModalOpen,
    selectedContact,
    handleOpenEditModal,
    handleCloseEditModal,
    handleUpdateContact,
    showQmDrawer,
    setShowQmDrawer,
  } = useContactEdit(onContactUpdated)

  const isDisabledQmDrawer = (contact: Contact) => {
    return contact.status === STATUS_CONTACT.UNVERIFIED
  }
  const navigate = useNavigate()

  const [saleModalContact, setSaleModalContact] = useState<Contact | null>(null)
  const [voiceModalContact, setVoiceModalContact] = useState<Contact | null>(null)
  const [showVoiceModal, setShowVoiceModal] = useState(false)
  const [portalMenuAnchor, setPortalMenuAnchor] = useState<HTMLElement | null>(null)
  const [portalContact, setPortalContact] = useState<Contact | null>(null)

  // Detect microphone availability and notify user if not present
  const hasWarnedNoMicRef = useRef(false)

  useEffect(() => {
    const checkMic = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return
        const devices = await navigator.mediaDevices.enumerateDevices()
        const hasMic = devices.some((d) => d.kind === 'audioinput')
        if (!hasMic && !hasWarnedNoMicRef.current) {
          hasWarnedNoMicRef.current = true
          toast.error(t('COMMON.ERROR.NO_MICROPHONE'))
        }
      } catch (_) {
        // silently ignore
      }
    }

    checkMic()
    if (navigator.mediaDevices && 'addEventListener' in navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', checkMic)
      return () => {
        navigator.mediaDevices.removeEventListener('devicechange', checkMic)
      }
    }
  }, [t])

  // Reinitialize Metronic menus when contacts list changes
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        MenuComponent.reinitialization()
      } catch (_) {
        // ignore
      }
    }, 200)
    return () => clearTimeout(timer)
  }, [contacts])

  const handleDeleteClick = async (contactId: string) => {
    setSelectedContacts([contactId])
    await onDeleteContacts?.()
  }

  const sanitizePhone = (raw?: string) => String(raw || '').replace(/\D/g, '')

  // Removed unused renderTruncatedText helper; inlined usage in columns
  const renderCell = (contact: Contact, column: TableColumn, index: number) => {
    if (column.key === FIELD_SELECTED) {
      const isVerified = contact.status === 'verified'
      return (
        <div
          className='form-check form-check-sm form-check-custom'
          title={isVerified ? t('RAW_CONTACT_COLLECTION.VERIFIED_LABEL') : ''}
        >
          <input
            className='form-check-input'
            type='checkbox'
            checked={selectedContacts.includes(contact.id)}
            onChange={() => onSelectContact(contact.id)}
            disabled={isLoading}
          />
        </div>
      )
    }
    if (column.key === FIELD_TAGS) {
      return (
        <div className='d-flex flex-wrap gap-1'>
          {contact.tags?.map((tag) => (
            <span key={tag._id} className='badge badge-light border fw-bold text-gray-800'>
              {tag.name.toUpperCase()}
            </span>
          ))}
        </div>
      )
    }

    if (column.key === FIELD_STATUS) {
      // Render textual statuses with color similar to mock: in-flow (green), paused (gray), unverified (orange)
      const statusValue = (contact.status || '')?.toLowerCase()
      let colorClass = 'text-muted'
      if (statusValue === 'in-flow') colorClass = 'text-success'
      else if (statusValue === 'paused') colorClass = 'text-gray-500'
      else if (statusValue === 'unverified') colorClass = 'text-warning'

      return (
        <span className={`${colorClass} fw-bold table-text-truncate-3-lines`}>
          {contact.status}
        </span>
      )
    }

    if (column.key === FIELD_SALES) {
      const isSale = Boolean(contact.sales)
      const tooltip = isSale
        ? 'Deal Status: SOLD - Click to change'
        : 'Deal Status: NOT SOLD - Click to mark as sold'
      return (
        <OverlayTrigger
          placement='top'
          delay={{show: 0, hide: 0}}
          overlay={<Tooltip>{tooltip}</Tooltip>}
        >
          <span
            onClick={() => {
              setSaleModalContact(contact)
            }}
            style={{cursor: 'pointer'}}
          >
            <KTSVG
              path='/media/icons/duotune/general/gen043.svg'
              className={`svg-icon-2 ${isSale ? 'text-success fw-bold' : 'text-gray-400'}`}
            />
          </span>
        </OverlayTrigger>
      )
    }

    // Handle Last Outbound Message with date + days since
    if (column.key === FIELD_OUTBOUND_MESSAGE) {
      if (contact.outboundMessage) {
        return (
          <div className='d-flex flex-column'>
            <span className='text-gray-800 fw-bold table-date-main table-text-truncate-3-lines'>
              {contact.outboundMessage}
            </span>
          </div>
        )
      }
      return <span className='text-muted'>-</span>
    }

    if (column.render) {
      return column.render(contact[column.key as keyof Contact])
    }

    if (column.key === FIELD_EMAIL) {
      return contact[column.key as keyof Contact] ? (
        <span className='table-text-truncate-3-lines'>{contact[column.key as keyof Contact]}</span>
      ) : (
        <>&nbsp;</>
      )
    }

    if (column.key === FIELD_PHONE) {
      if (contact[column.key as keyof Contact]) {
        const phoneNumber = contact[column.key as keyof Contact] as string
        // Format phone number with dashes (e.g., '412345678' -> '412-345-678')
        const formattedPhone = phoneNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1-$2-$3')
        return (
          <span className='table-text-truncate-3-lines'>
            {contact.countryCode + ' ' + formattedPhone}
          </span>
        )
      }
      return <>&nbsp;</>
    }
    if (column.key === FIELD_SOURCE_DETAIL) {
      return <span className='table-text-truncate-3-lines'>{contact.cardName}</span>
    }

    if (column.key === FIELD_REPLIES) {
      return (
        <span className='badge badge-dark rounded-pill text-white px-3 py-1'>
          {contact.replies}
        </span>
      )
    }

    if (column.key === FIELD_PROMO_ACTION) {
      return (
        <div className='d-flex flex-column gap-1'>
          {contact?.promoSettings?.map((promo: PromoAction) => (
            <span
              key={promo.id}
              className='border rounded-pill px-3 py-1 text-gray-800 fw-normal bg-white text-nowrap promo-badge'
            >
              {promo.name}
            </span>
          ))}
        </div>
      )
    }

    if (column.key === FIELD_FLOW) {
      return (
        <>
          {contact.flows && contact.flows.length > 0 ? (
            <div className='flow-badges'>
              {contact.flows.map((flow) => (
                <span
                  className={`w-100 border rounded-pill px-3 py-1 fw-normal text-nowrap cursor-pointer d-inline-flex align-items-center justify-content-start me-2 flow-badge ${
                    flow.status === 'active'
                      ? 'text-primary bg-white border-primary'
                      : 'text-muted bg-light border-secondary'
                  }`}
                  onClick={() => {
                    if (flow) {
                      navigate(`/report-analytics/flow/report/${flow.id}`)
                    }
                  }}
                  title={flow.name}
                >
                  <i
                    className={`bi ${
                      flow.status === 'active' ? 'bi-play-fill' : 'bi-pause-fill'
                    } me-1`}
                  ></i>
                  {flow.name}
                </span>
              ))}
            </div>
          ) : (
            <>&nbsp;</>
          )}
        </>
      )
    }

    if (column.key === FIELD_ASSIGN_TO) {
      return contact.assignedTo ? (
        <span className='text-gray-800 fw-bold table-text-truncate-3-lines'>
          {contact.assignedTo.name}
        </span>
      ) : (
        <span className='text-muted'>-</span>
      )
    }

    // Handle new columns according to feedback
    if (column.key === FIELD_FULL_NAME) {
      const {shouldPulseMessage, shouldPulseVoice} = getAttentionStates(contact)
      const isSold = Boolean(contact.sales)
      return (
        <span className='d-inline-flex align-items-center'>
          {isSold ? (
            <OverlayTrigger
              placement='top'
              delay={{show: 0, hide: 0}}
              overlay={<Tooltip>Deal Status: SOLD</Tooltip>}
            >
              <i
                className='bi bi-patch-check-fill text-success me-2'
                aria-hidden='true'
                style={{fontSize: '1.6em'}}
              ></i>
            </OverlayTrigger>
          ) : (
            (shouldPulseMessage || shouldPulseVoice) && (
              <OverlayTrigger
                placement='top'
                delay={{show: 0, hide: 0}}
                overlay={
                  <Tooltip>
                    {shouldPulseMessage
                      ? 'New reply received, but no message or call has been sent—please take action.'
                      : 'Add a voice note for this contact.'}
                  </Tooltip>
                }
              >
                <img
                  src='/media/svg/triangle_notify.svg'
                  alt='Attention'
                  className='me-2'
                  style={{
                    width: '1.6em',
                    height: '1.6em',
                    animation: 'iconSoftPulse 1.6s ease-in-out infinite',
                  }}
                />
              </OverlayTrigger>
            )
          )}
          <span className='text-gray-800 fw-bold table-text-truncate-3-lines'>
            {contact.fullName || '-'}
          </span>
        </span>
      )
    }

    if (column.key === FIELD_CARD_OFFERS) {
      // Display selected card offers (promo settings)
      return (
        <div className='d-flex flex-column gap-1'>
          {contact?.promoSettings?.map((promo: PromoAction) => (
            <span
              key={promo.id}
              className='border rounded-pill px-2 py-1 text-gray-800 fw-normal bg-light text-nowrap card-offer-badge'
              style={{width: 'fit-content'}}
            >
              {promo.name}
            </span>
          ))}
          {(!contact?.promoSettings || contact.promoSettings.length === 0) && (
            <span className='text-muted'>-</span>
          )}
        </div>
      )
    }

    if (column.key === FIELD_INTENT) {
      // Intent level styled like RecordList: plain bold text
      const intentText = contact.intent?.toUpperCase() || '-'
      return (
        <span className='text-gray-800 fw-bold table-text-truncate-3-lines w-100'>
          {intentText}
        </span>
      )
    }

    if (column.key === FIELD_SENTIMENT) {
      // Sentiment from voice analysis - Positive/Negative/Neutral
      const sentimentType = contact.sentiment
      let badgeClass = ''

      switch (sentimentType?.toLowerCase()) {
        case 'positive':
          badgeClass = 'badge sentiment-badge positive'
          break
        case 'negative':
          badgeClass = 'badge sentiment-badge negative'
          break
        case 'neutral':
          badgeClass = 'badge sentiment-badge neutral'
          break
      }

      return <span className={`badge ${badgeClass}`}>{sentimentType?.toUpperCase() || '-'}</span>
    }

    if (column.key === FIELD_VOICE_NOTES_SUMMARY) {
      // Voice Notes Summary from AI analysis
      const summaryText = (contact.aiSummaryNote || '').toString()
      const isEmpty = !summaryText.trim()
      if (isEmpty) {
        return (
          <span
            className='text-muted cursor-pointer border rounded-pill px-2 py-1 bg-white'
            onClick={() => {
              setVoiceModalContact(contact)
              setShowVoiceModal(true)
            }}
          >
            {'Add AI Voice Notes'}
          </span>
        )
      }
      const limit = 150
      const isLongText = summaryText.length - limit > 5
      const displayText = isLongText ? summaryText.slice(0, limit) + '...' : summaryText
      return (
        <>
          <span
            className='cursor-pointer table-text-truncate-3-lines'
            title={summaryText}
            onClick={() => navigate(`/record-list/${contact.id}`)}
          >
            {displayText}
          </span>
          {isLongText && (
            <span className='see-more-btn' onClick={() => navigate(`/record-list/${contact.id}`)}>
              {t('VOICE_RECORDS.SEE_MORE')}
            </span>
          )}
        </>
      )
    }

    if (column.key === FIELD_NOTES_PREVIEW) {
      // Clickable notes preview
      const notesText = (contact.noteText || contact.noteVoice || '').toString()
      const isEmpty = !notesText.trim()
      if (isEmpty) {
        return (
          <span
            className='text-muted cursor-pointer border rounded-pill px-2 py-1 bg-white'
            onClick={() => {
              setVoiceModalContact(contact)
              setShowVoiceModal(true)
            }}
          >
            {'Add notes'}
          </span>
        )
      }
      const limit = 150
      const isLongText = notesText.length - limit > 5
      const displayText = isLongText ? notesText.slice(0, limit) + '...' : notesText
      return (
        <>
          <span
            className='cursor-pointer table-text-truncate-3-lines'
            title={notesText}
            onClick={() => handleOpenEditModal(contact)}
          >
            {displayText}
          </span>
          {isLongText && (
            <span className='see-more-btn' onClick={() => handleOpenEditModal(contact)}>
              {t('VOICE_RECORDS.SEE_MORE')}
            </span>
          )}
        </>
      )
    }

    if (column.key === FIELD_LAST_REPLY) {
      // Show last reply date + days from message
      if (contact.lastMessagePreview) {
        return (
          <div className='d-flex flex-column'>
            <span className='text-gray-800 fw-bold table-date-main table-text-truncate-3-lines'>
              {contact.lastMessagePreview}
            </span>
          </div>
        )
      }
      return <span className='text-muted'>-</span>
    }

    if (column.key === FIELD_TOTAL_MESSAGES_SENT) {
      // Show total messages sent count
      return (
        <span className='badge badge-secondary rounded-pill text-white px-2 py-1'>
          {contact.countSentMessages || 0}
        </span>
      )
    }

    if (column.key === FIELD_SOURCE) {
      // Source: B-card, Lead form, LiveChat, Manually added, API
      return (
        <span className='text-gray-800 table-text-truncate-3-lines'>
          {contact.source || contact.cardName || '-'}
        </span>
      )
    }

    if (column.key === FIELD_ACTION) {
      const disabledMsg = isDisabledQmDrawer ? isDisabledQmDrawer(contact) : false
      const phone = sanitizePhone(contact.phone)
      const {shouldPulseMessage, shouldPulseVoice} = getAttentionStates(contact)
      return (
        <div className='d-flex justify-content-center align-items-center gap-2 action-buttons'>
          {/* BTN1: Call */}
          <span
            title={t('RAW_CONTACT_COLLECTION.CALL') || 'Call - Click to dial this contact'}
            className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary btn-action-table'
            onClick={() => {
              if (!phone) return toast.error(t('COMMON.ERROR.NO_PHONE') || 'No phone number found')
              window.open(`tel:${contact.countryCode + phone}`, '_self')
              try {
                const nowIso = new Date().toISOString()
                handleUpdateContact({id: contact.id, callLastTime: nowIso} as any)
              } catch (_) {}
            }}
          >
            <i className='bi bi-telephone'></i>
          </span>

          {/* BTN2: Message -> Inbox */}
          {AuthenticateUser('send-quick-sms') && (
            <div className={shouldPulseMessage ? 'flash-orange rounded' : ''}>
              <span
                title={
                  disabledMsg
                    ? 'Message - Contact not verified'
                    : t('RAW_CONTACT_COLLECTION.SEND_QUICK_MESSAGE') ||
                      'Message - Send SMS to this contact'
                }
                className={`btn btn-sm btn-icon btn-bg-light btn-active-color-primary btn-action-table ${
                  disabledMsg ? 'action-btn-disabled' : ''
                }`}
                onClick={() => {
                  if (disabledMsg) return
                  if (!phone)
                    return toast.error(t('COMMON.ERROR.NO_PHONE') || 'No phone number found')
                  navigate({pathname: '/inbox', search: `?receiver=${phone}`})
                }}
                aria-disabled={disabledMsg}
                tabIndex={disabledMsg ? -1 : 0}
              >
                <i className='bi bi-send'></i>
              </span>
            </div>
          )}

          {/* BTN3: Voice record */}
          <div className={shouldPulseVoice ? 'flash-orange rounded' : ''}>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary btn-action-table'
              title={
                t('RAW_CONTACT_COLLECTION.START_RECORDING') ||
                'Voice Record - Record voice note for this contact'
              }
              onClick={(e) => {
                try {
                  ;(e.currentTarget as HTMLButtonElement).blur()
                } catch (_) {}
                setVoiceModalContact(contact)
                setShowVoiceModal(true)
              }}
            >
              <i className='bi bi-mic-fill'></i>
            </button>
          </div>

          {/* BTN4: More actions via Portal (cross-browser, no clipping) */}
          <span
            className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary ms-3 btn-action-table more-action-icon-btn'
            data-kt-menu-trigger='hover'
            data-kt-menu-placement='bottom-end'
            data-kt-menu-overflow='false'
            title='More Actions - Edit, View Voice Notes, Delete'
            onClick={(e) => {
              e.stopPropagation()
              setPortalMenuAnchor(e.currentTarget as HTMLElement)
              setPortalContact(contact)
            }}
            onMouseEnter={(e) => {
              setPortalMenuAnchor(e.currentTarget as HTMLElement)
              setPortalContact(contact)
            }}
          >
            <span className='more-action-icon'>...</span>
          </span>
        </div>
      )
    }
    const cellValue = contact[column.key as keyof Contact]
    if (typeof cellValue === 'string' && cellValue.trim()) {
      return <span className='table-text-truncate-3-lines'>{cellValue}</span>
    }
    return cellValue
  }

  const topScrollWrapperRef = useRef<HTMLDivElement>(null)
  const topScrollRef = useRef<HTMLDivElement>(null)
  const tableResponsiveRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)

  const getAndSetWidth = useCallback(() => {
    if (
      !scrollOnTop ||
      !topScrollWrapperRef.current ||
      !tableResponsiveRef.current ||
      !tableRef.current
    )
      return

    const wrapperWidth = tableResponsiveRef.current.offsetWidth + 'px'
    const scrollWidth = tableRef.current.offsetWidth + 'px'

    if (topScrollWrapperRef.current) {
      topScrollWrapperRef.current.style.width = wrapperWidth
    }
    if (topScrollRef.current) {
      topScrollRef.current.style.width = scrollWidth
    }
  }, [scrollOnTop])

  const handleTopScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (tableResponsiveRef.current) {
      tableResponsiveRef.current.scrollLeft = e.currentTarget.scrollLeft
    }
  }, [])

  const handleTableScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (topScrollWrapperRef.current) {
      topScrollWrapperRef.current.scrollLeft = e.currentTarget.scrollLeft
    }
  }, [])

  useEffect(() => {
    if (!scrollOnTop) return

    getAndSetWidth()

    const handleResize = () => {
      getAndSetWidth()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [scrollOnTop, getAndSetWidth, contacts, columns])

  const SendQuickMessage = useMemo(
    () => (
      <QuickMessageDrawerInner
        show={showQmDrawer}
        handleClose={() => setShowQmDrawer(false)}
        contacts={[
          {
            label: selectedContact?.phone,
            value: selectedContact?.phone,
          },
        ]}
      />
    ),
    [selectedContact?.phone, setShowQmDrawer, showQmDrawer]
  )

  return (
    <>
      {portalMenuAnchor && portalContact && (
        <ActionMenuPortal
          anchorEl={portalMenuAnchor}
          onClose={() => setPortalMenuAnchor(null)}
          items={[
            {
              iconClass: 'bi bi-pencil me-2',
              label: t('RAW_CONTACT_COLLECTION.EDIT_PROFILE') || 'Edit profile',
              onClick: () => handleOpenEditModal(portalContact),
            },
            {
              iconClass: 'bi bi-music-note-list me-2',
              label: t('RAW_CONTACT_COLLECTION.VIEW_VOICE_AI_NOTES') || 'View Voice AI Notes',
              onClick: () => navigate(`/record-list/${portalContact.id}`),
            },
            {
              iconClass: 'bi bi-trash me-2',
              label: t('RAW_CONTACT_COLLECTION.DELETE_CONTACT') || 'Delete contact',
              onClick: () => handleDeleteClick(portalContact.id),
              danger: true,
            },
          ]}
        />
      )}
      <div
        className='table-container position-relative table-container-dynamic'
        style={{minHeight}}
      >
        {SendQuickMessage}
        <VoiceRecordModal
          show={showVoiceModal}
          contact={voiceModalContact}
          onClose={() => setShowVoiceModal(false)}
          onSaved={() => {
            try {
              onContactUpdated && onContactUpdated()
              navigate(`/record-list/${voiceModalContact?.id}`)
              setShowVoiceModal(false)
            } catch (_) {}
          }}
        />
        {isLoading && (
          <div className='loading-overlay'>
            <div className='loading-spinner-container'>
              <Spinner animation='border' role='status'>
                <span className='visually-hidden'>{t('COMMON.LOADING')}</span>
              </Spinner>
            </div>
          </div>
        )}

        {scrollOnTop && (
          <div ref={topScrollWrapperRef} className='top-scroll-wrapper' onScroll={handleTopScroll}>
            <div ref={topScrollRef} className='top-scroll' />
          </div>
        )}

        <div ref={tableResponsiveRef} className='table-responsive' onScroll={handleTableScroll}>
          <Table
            ref={tableRef}
            hover
            className={`contact-table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4 ${
              isLoading ? 'loading' : ''
            }`}
          >
            <ContactTableHeader
              columns={columns}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={onSort}
              isLoading={isLoading}
              selectedAll={selectedAll}
              onSelectAll={onSelectAll}
              hasSelectableContacts={true}
              onColumnReorder={onColumnReorder}
            />
            <tbody>
              {contacts.length > 0 ? (
                contacts.map((contact, index) => (
                  <tr
                    key={index}
                    className={`${contact.status === 'verified' ? 'row-verified' : ''}`}
                  >
                    {columns.map((column: TableColumn) => (
                      <td
                        key={column.key}
                        className={`border-bottom ${column.className || column.key} ${
                          column.key === FIELD_SELECTED ? 'bg-white' : ''
                        }`}
                        style={{
                          width: column.width,
                        }}
                      >
                        {renderCell(contact, column, index)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className='text-center py-4'>
                    {noDataMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>{' '}
      {/* close table-container */}
      <ContactEditModal
        show={isEditModalOpen}
        contact={selectedContact}
        onClose={handleCloseEditModal}
        onUpdate={handleUpdateContact}
        onVoiceSaved={() => {
          try {
            onContactUpdated && onContactUpdated()
          } catch (_) {}
        }}
      />
      {saleModalContact && (
        <SaleStatusModal
          show={!!saleModalContact}
          contact={saleModalContact}
          onClose={() => setSaleModalContact(null)}
          onSave={(newStatus) => {
            if (onToggleSale) {
              onToggleSale(saleModalContact, newStatus)
            }
            setSaleModalContact(null)
          }}
        />
      )}
    </>
  )
}
